import { format, formatDistance } from 'date-fns';

// Re-export only what exists in formatters
export { formatDate, formatRelativeTime } from './formatters';

// Add formatDateTime here if you need it
export const formatDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'MMM dd, yyyy HH:mm');
};

export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800',
    pending_approval: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getPriorityColor = (priority: string): string => {
  const colors: Record<string, string> = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-blue-100 text-blue-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800';
};

export const getFuelLevelColor = (percentage: number): string => {
  if (percentage < 10) return 'bg-red-500';
  if (percentage < 25) return 'bg-orange-500';
  if (percentage < 50) return 'bg-yellow-500';
  return 'bg-green-500';
};

export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
};

export const sortBy = <T>(array: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
};

export const calculateDaysSince = (date: Date | string | null | undefined): number | null => {
  if (!date) return null;
  const now = new Date();
  const past = new Date(date);
  const diff = now.getTime() - past.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export const getImageUrl = (path: string | undefined): string => {
  if (!path) return '/placeholder-image.png';
  
  // If already a full URL, return as-is
  if (/^https?:\/\//i.test(path)) return path;

  // Get base URL from environment or use default
  // Make sure to use window.location.origin as fallback for client-side
  let baseUrl = process.env.REACT_APP_UPLOAD_URL;
  
  if (!baseUrl) {
    // Fallback to API URL without /api suffix, or localhost:5000
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      baseUrl = apiUrl.replace(/\/api\/?$/, '');
    } else {
      baseUrl = 'http://localhost:5000';
    }
  }
  
  // Normalize path to ensure it starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Construct full URL with proper format
  const fullUrl = `${baseUrl}${normalizedPath}`;
  
  // Debug logging (only log once per unique URL to avoid spam)
  if (process.env.NODE_ENV === 'development') {
    console.log('getImageUrl:', { 
      original: path, 
      normalized: normalizedPath, 
      baseUrl, 
      fullUrl,
      env: {
        REACT_APP_UPLOAD_URL: process.env.REACT_APP_UPLOAD_URL,
        REACT_APP_API_URL: process.env.REACT_APP_API_URL
      }
    });
  }
  
  return fullUrl;
};

export const classNames = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};



